import { DOCUMENT_LIBRARY_COPY } from '#app/routes/mocra+/_form+/components/document-libraries/constants.ts'
import { type DocumentLibraryEntry } from '#app/routes/mocra+/_form+/components/document-libraries/schema.ts'
import {
	DOCUMENT_LIBRARY_SYSTEM_OPTIONS,
	DOCUMENT_SCOPE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type DocumentLibrarySystemValue =
	(typeof DOCUMENT_LIBRARY_SYSTEM_OPTIONS)[number]['value']

export type DocumentLibraryScopeValue =
	(typeof DOCUMENT_SCOPE_OPTIONS)[number]['value']

export type DocumentLibraryDraft = {
	system: DocumentLibrarySystemValue
	scope: DocumentLibraryScopeValue
	location: string
}

export function createDocumentLibraryDraft(
	overrides: Partial<DocumentLibraryDraft> = {},
): DocumentLibraryDraft {
	return {
		system: DOCUMENT_LIBRARY_SYSTEM_OPTIONS[0]?.value ?? 'qms',
		scope: DOCUMENT_SCOPE_OPTIONS[0]?.value ?? 'sops',
		location: '',
		...overrides,
	}
}

export function sanitizeDocumentLibraryDraft(
	draft: DocumentLibraryDraft,
): DocumentLibraryDraft {
	const system = isDocumentLibrarySystemValue(draft.system)
		? draft.system
		: (DOCUMENT_LIBRARY_SYSTEM_OPTIONS[0]?.value ?? 'qms')
	const scope = isDocumentLibraryScopeValue(draft.scope)
		? draft.scope
		: (DOCUMENT_SCOPE_OPTIONS[0]?.value ?? 'sops')
	return {
		system,
		scope,
		location: draft.location.trim(),
	}
}

export function collectDocumentLibraryErrors(
	draft: DocumentLibraryDraft,
): string[] {
	const sanitized = sanitizeDocumentLibraryDraft(draft)
	const errors: string[] = []
	if (sanitized.location.length === 0) {
		errors.push('Provide the folder path or link.')
	}
	return errors
}

export function documentLibraryCardTitle(draft: DocumentLibraryDraft): string {
	const sanitized = sanitizeDocumentLibraryDraft(draft)
	return resolveDocumentScopeLabel(sanitized.scope)
}

export function formatDocumentLibrarySummary(
	draft: DocumentLibraryDraft,
): string {
	const sanitized = sanitizeDocumentLibraryDraft(draft)
	const location =
		sanitized.location || DOCUMENT_LIBRARY_COPY.summary.locationMissing
	const systemLabel = resolveDocumentLibrarySystemLabel(sanitized.system)
	return `${location} — ${systemLabel}`
}

export function resolveDocumentLibrarySystemLabel(
	value: DocumentLibrarySystemValue,
): string {
	return (
		DOCUMENT_LIBRARY_SYSTEM_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function resolveDocumentScopeLabel(
	value: DocumentLibraryScopeValue,
): string {
	return (
		DOCUMENT_SCOPE_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function isDocumentLibrarySystemValue(
	value: unknown,
): value is DocumentLibrarySystemValue {
	return DOCUMENT_LIBRARY_SYSTEM_OPTIONS.some(
		(option) => option.value === value,
	)
}

export function isDocumentLibraryScopeValue(
	value: unknown,
): value is DocumentLibraryScopeValue {
	return DOCUMENT_SCOPE_OPTIONS.some((option) => option.value === value)
}

export function documentLibraryDraftToEntry(
	draft: DocumentLibraryDraft,
): DocumentLibraryEntry {
	const sanitized = sanitizeDocumentLibraryDraft(draft)
	return {
		system: sanitized.system as DocumentLibraryEntry['system'],
		scope: sanitized.scope as DocumentLibraryEntry['scope'],
		location: sanitized.location,
	}
}

export function documentLibraryEntryToDraft(
	entry: DocumentLibraryEntry,
): DocumentLibraryDraft {
	const system = isDocumentLibrarySystemValue(entry.system)
		? entry.system
		: undefined
	const scope = isDocumentLibraryScopeValue(entry.scope)
		? entry.scope
		: undefined
	return sanitizeDocumentLibraryDraft(
		createDocumentLibraryDraft({
			system,
			scope,
			location: entry.location,
		}),
	)
}
